import React, { useCallback, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from "@mui/material";
import { getResponsiveDialogProps } from "../../theme/responsive";

/**
 * Converts arbitrary user input into a valid URL / DNS slug:
 *   "My Awesome Blog" → "my-awesome-blog"
 *   "test website"    → "test-website"
 *   "Test_Site!"      → "test-site"
 */
const sanitizeSlug = (raw) =>
  raw
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "-") // spaces → hyphens
    .replace(/[^a-z0-9-]/g, "") // strip non-alphanumeric except hyphens
    .replace(/-+/g, "-") // collapse consecutive hyphens
    .replace(/^-+|-+$/g, ""); // trim leading / trailing hyphens

const CreateSiteDialog = ({ open, onClose, onCreate, newSite, setNewSite }) => {
  const handleNameChange = useCallback(
    (e) => {
      const raw = e.target.value;
      setNewSite((prev) => ({ ...prev, name: raw }));
    },
    [setNewSite],
  );

  const handleNameBlur = useCallback(() => {
    // Sanitize when the user leaves the field so the value is clean
    setNewSite((prev) => ({
      ...prev,
      name: sanitizeSlug(prev.name),
    }));
  }, [setNewSite]);

  const handleEnvChange = useCallback(
    (e) => setNewSite((prev) => ({ ...prev, environment: e.target.value })),
    [setNewSite],
  );

  // Slug preview computed from current input
  const slug = useMemo(() => sanitizeSlug(newSite.name || ""), [newSite.name]);

  // Detect when the raw value differs from its sanitized form
  const hasInvalidChars = newSite.name !== "" && newSite.name !== slug;

  const isNameValid = slug.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      {...getResponsiveDialogProps("medium")}
    >
      <DialogTitle>
        <Typography variant="h6" fontWeight={600}>
          Create New WordPress Site
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          <Box>
            <TextField
              fullWidth
              label="Site Name"
              placeholder="my-awesome-blog"
              value={newSite.name}
              onChange={handleNameChange}
              onBlur={handleNameBlur}
              error={newSite.name !== "" && !isNameValid}
              helperText={
                hasInvalidChars
                  ? `Will be saved as: "${slug}"`
                  : slug
                    ? `Site slug: ${slug}`
                    : "Use lowercase letters, numbers, and hyphens only"
              }
              inputProps={{ maxLength: 63 }}
            />

            {hasInvalidChars && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Spaces and special characters are not allowed in site names.
                They will be automatically converted — your site will be created
                as &quot;{slug}&quot;.
              </Alert>
            )}
          </Box>

          <FormControl fullWidth>
            <InputLabel>Environment</InputLabel>
            <Select
              value={newSite.environment}
              label="Environment"
              onChange={handleEnvChange}
            >
              <MenuItem value="SANDBOX">Sandbox</MenuItem>
              <MenuItem value="STAGING">Staging</MenuItem>
              <MenuItem value="PRODUCTION">Production</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={() => {
            // Always apply the sanitized slug before creating
            const finalName = sanitizeSlug(newSite.name || "");
            setNewSite((prev) => ({ ...prev, name: finalName }));
            // Let the state update propagate, then trigger creation
            setTimeout(onCreate, 0);
          }}
          disabled={!isNameValid || !newSite.environment}
        >
          Create Site
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default React.memo(CreateSiteDialog);
